import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import type { GridApi, ICellRendererParams } from 'ag-grid-community';
import { AlertCircleOutline } from '@opengov/react-capital-assets';
import { EGroupType } from '@opengov/reporting-types';
import get from 'lodash/get';
import React, { useState } from 'react';
import { LocaleText } from '../../../locale';
import { FxBadge } from '../../editor/sidepanel/Calculations/FxBadge';
import { getCurrentAgGridContext, selectNodeAndChildren } from '../agGridConsts';
import { useIsEditing } from '../useIsEditing';
import rawStyles from './MdsRowGroupHeader.module.scss';
import { sxStyles } from './MdsRowGroupHeader.styles';

const styles = rawStyles as Record<string, string>;

// Workaround for issue with value as empty text. This keeps the cell
// layout intact with edit button visible.
const nbsp = '\u00A0';

const intl = {
  rowFailedToLoad: 'Error loading row',
  groupDeleted: 'This group has been deleted',
  breakdownDeletedTooltip:
    "A group used by this row no longer exists. Update this row's configuration to remove this warning.",
  parentDeletedTooltip: "Update this row's configuration to remove this warning."
};

function getDisplayValue({
  isLoadError,
  deletedGroupLevel,
  rowName,
  rawGroupId
}: {
  isLoadError: boolean | undefined;
  deletedGroupLevel: 'parent' | 'breakdown' | undefined;
  rowName: string;
  rawGroupId: string | number | undefined;
}): string {
  if (isLoadError) {
    return intl.rowFailedToLoad;
  }
  if (deletedGroupLevel !== 'parent') {
    return rowName;
  }
  const hasCustomName = rowName !== String(rawGroupId) && rowName !== nbsp;
  if (hasCustomName) {
    return `${rowName} \u2014 ${intl.groupDeleted}`;
  }
  return intl.groupDeleted;
}
type MdsRowGroupHeaderRowData = {
  rowGroupIndex: number;
  placeholder?: boolean;
  groupId?: string | number;
  isBreakdownRow?: boolean;
  isRootLevelBreakdown?: boolean;
  breakdownLevel?: number;
  groupData?: { type?: string };
  canExpand?: boolean;
  isLoadError?: boolean;
  deletedGroupLevel?: 'parent' | 'breakdown';
};

export interface IMdsRowGroupHeaderProps extends ICellRendererParams {
  onEditClick: (rowGroupIndex: number, lensId: string, groupId: string | undefined, rowLabel: string) => void;
  expandCollapseOneLevelEnabled: boolean;
  localeText: LocaleText['multidataset'];
}

export const MdsRowGroupHeader: React.FC<IMdsRowGroupHeaderProps> = (props) => {
  const { api, node, onEditClick, valueFormatted, expandCollapseOneLevelEnabled, localeText } = props;
  const mhContext = props.context as {
    onExpandRow: (params: { api: GridApi; node: unknown }) => void;
    onCollapseRow: (params: { api: GridApi; node: unknown }) => void;
  };

  const isEditing = useIsEditing();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const rowData = node.data as MdsRowGroupHeaderRowData | null | undefined;

  if (!rowData) {
    return (
      <Box className={styles.MdsRowGroupHeader}>
        <Box className={styles.text}>{nbsp}</Box>
      </Box>
    );
  }

  // TODO: Removed destructing inEditMode from getCurrentAgGridContext until state management resolve in REP-8620
  const { currentRowLensesId } = getCurrentAgGridContext(props);
  const { onExpandRow, onCollapseRow } = mhContext;
  const rowGroupIndex = rowData.rowGroupIndex;

  const isPlaceholder = rowData.placeholder;
  const isBreakdownRow = Boolean(rowData.isBreakdownRow || rowData.isRootLevelBreakdown);
  const canEditRow = isEditing && node.level === 0 && !isBreakdownRow;
  const isExpressionRow = rowData.groupData?.type === EGroupType.Expression;
  const isExpandable = !isExpressionRow && get(rowData, 'canExpand', false);
  const isLoadError = get(rowData, 'isLoadError', false);
  const deletedGroupLevel: 'parent' | 'breakdown' | undefined = get(rowData, 'deletedGroupLevel');
  const isCalculatedRow = get(rowData, 'groupData.type') === EGroupType.MultiHierarchyCalculated;

  const open = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditRowClick = () => {
    selectNodeAndChildren(api, node);
    const groupId = rowData.groupId;
    const parentGroupId = groupId == null ? undefined : String(groupId);
    onEditClick(rowGroupIndex, currentRowLensesId, parentGroupId, displayValue);
  };

  const rowName = valueFormatted ?? (props.value as string | undefined) ?? nbsp;
  const displayValue = getDisplayValue({ isLoadError, deletedGroupLevel, rowName, rawGroupId: rowData.groupId });

  return (
    <Box className={isEditing ? styles.MdsRowGroupHeaderEdit : styles.MdsRowGroupHeader}>
      <Box className={styles.textAndEditButton}>
        <Tooltip title={displayValue} placement="top">
          <Box className={styles.text}>{displayValue}</Box>
        </Tooltip>
        {deletedGroupLevel ? (
          <Tooltip
            title={deletedGroupLevel === 'breakdown' ? intl.breakdownDeletedTooltip : intl.parentDeletedTooltip}
            placement="top"
          >
            <Box sx={sxStyles.groupNotFoundIconWrapper} data-test="deletedGroupWarningIcon">
              <SvgIcon component={AlertCircleOutline} inheritViewBox fontSize="small" sx={sxStyles.groupNotFoundIcon} />
            </Box>
          </Tooltip>
        ) : null}
        {canEditRow && !isPlaceholder && (
          <Tooltip title="Show Row Editor">
            <IconButton
              aria-label={`edit row ${rowGroupIndex}`}
              size="small"
              className={styles.button}
              onClick={handleEditRowClick}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {canEditRow && isCalculatedRow ? <FxBadge /> : null}
      {!isPlaceholder && isExpandable && expandCollapseOneLevelEnabled && (
        <Box>
          <IconButton
            data-test={`expand-collapse-kebab-${node.id}`}
            aria-label="more"
            size="small"
            className={styles.expandCollapseKebab}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuItem
              onClick={() => {
                onExpandRow({ api, node });
                handleMenuClose();
              }}
            >
              {localeText.expandCollapse.expandOneLevel}
            </MenuItem>
            <MenuItem
              onClick={() => {
                onCollapseRow({ api, node });
                handleMenuClose();
              }}
            >
              {localeText.expandCollapse.collapseOneLevel}
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};
